import React, { useState, useEffect, useRef } from 'react';
import SharedCard from './SharedCard';
import SharedButton from './SharedButton';

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

// Rubros de academia de danza con referencias de la industria
const DEFAULT_RUBROS = [
  { id: 1,  name: 'Salarios Maestros / Instructores',  value: 0, reference: '25-35%', auto: true  },
  { id: 2,  name: 'Salarios Personal Administrativo',  value: 0, reference: '5-10%',  auto: false },
  { id: 3,  name: 'Arrendamiento del Estudio',          value: 0, reference: '15-25%', auto: false },
  { id: 4,  name: 'Servicios Públicos',                 value: 0, reference: '3-5%',   auto: false },
  { id: 5,  name: 'Uniformes y Artículos de Danza',     value: 0, reference: '3-6%',   auto: true  },
  { id: 6,  name: 'Publicidad y Marketing',             value: 0, reference: '3-5%',   auto: false },
  { id: 7,  name: 'Mantenimiento y Reparaciones',       value: 0, reference: '1-3%',   auto: false },
  { id: 8,  name: 'Seguros y Licencias',                value: 0, reference: '1-2%',   auto: false },
  { id: 9,  name: 'Honorarios Profesionales',           value: 0, reference: '1-3%',   auto: false },
  { id: 10, name: 'Gastos Financieros',                 value: 0, reference: '1-3%',   auto: false },
  { id: 11, name: 'Fondo de Reserva',                   value: 0, reference: '2-5%',   auto: false },
];

const FinancesPanel = () => {
  const [sales,          setSales]          = useState([]);
  const [savedReports,   setSavedReports]   = useState([]);
  const [startDate,      setStartDate]      = useState('');
  const [endDate,        setEndDate]        = useState('');
  const [filteredSales,  setFilteredSales]  = useState([]);
  const [totalSales,     setTotalSales]     = useState(0);
  const [rubros,         setRubros]         = useState(DEFAULT_RUBROS.map(r => ({ ...r })));
  const [msg,            setMsg]            = useState(null);

  // Rastrear qué rubros auto fueron editados manualmente
  const manuallyEdited = useRef(new Set());

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/list_sales.php',             { credentials: 'include' })
      .then(r => r.json()).then(d => setSales(Array.isArray(d) ? d : [])).catch(() => setSales([]));
    fetch('/api/list_financial_reports.php', { credentials: 'include' })
      .then(r => r.json()).then(d => setSavedReports(Array.isArray(d) ? d : [])).catch(() => setSavedReports([]));
  }, []);

  // ── Filtrar ventas ─────────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = sales;
    if (startDate) {
      const ts = new Date(startDate + 'T00:00:00').getTime();
      filtered = filtered.filter(s => {
        const parts = (s.date || '').split('/');
        if (parts.length === 3) {
          const [d, m, y] = parts;
          return new Date(`${y}-${m}-${d}T00:00:00`).getTime() >= ts;
        }
        return new Date(s.date).getTime() >= ts;
      });
    }
    if (endDate) {
      const ts = new Date(endDate + 'T23:59:59').getTime();
      filtered = filtered.filter(s => {
        const parts = (s.date || '').split('/');
        if (parts.length === 3) {
          const [d, m, y] = parts;
          return new Date(`${y}-${m}-${d}T00:00:00`).getTime() <= ts;
        }
        return new Date(s.date).getTime() <= ts;
      });
    }
    setFilteredSales(filtered);
    setTotalSales(filtered.reduce((sum, s) => sum + parseFloat(s.total || 0), 0));
  }, [sales, startDate, endDate]);

  // ── Cálculo automático: Salarios Maestros ──────────────────────────────────
  useEffect(() => {
    if (manuallyEdited.current.has(1)) return;
    if (!startDate || !endDate) {
      setRubros(prev => prev.map(r => r.id === 1 ? { ...r, value: 0 } : r));
      return;
    }
    fetch(`/api/sum_teacher_salaries.php?start=${startDate}&end=${endDate}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && !manuallyEdited.current.has(1)) {
          setRubros(prev => prev.map(r => r.id === 1 ? { ...r, value: data.total } : r));
        }
      }).catch(() => {});
  }, [startDate, endDate]);

  // ── Cálculo automático: Uniformes y Artículos ──────────────────────────────
  useEffect(() => {
    if (manuallyEdited.current.has(5)) return;
    if (!startDate || !endDate) {
      setRubros(prev => prev.map(r => r.id === 5 ? { ...r, value: 0 } : r));
      return;
    }
    fetch(`/api/sum_production_cost.php?start=${startDate}&end=${endDate}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && !manuallyEdited.current.has(5)) {
          setRubros(prev => prev.map(r => r.id === 5 ? { ...r, value: data.total } : r));
        }
      }).catch(() => {});
  }, [startDate, endDate]);

  // ── Cambio manual de un rubro ──────────────────────────────────────────────
  const handleRubroChange = (id, value) => {
    manuallyEdited.current.add(id);
    setRubros(prev => prev.map(r => r.id === id ? { ...r, value: Number(value) || 0 } : r));
  };

  // ── Calcular totales ───────────────────────────────────────────────────────
  const totalCostos   = rubros.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const utilidadNeta  = totalSales - totalCostos;

  // Estructura con porcentajes calculados
  const estructuraConPct = rubros.map(r => ({
    ...r,
    pct: totalSales > 0 ? (r.value / totalSales) * 100 : 0,
  }));
  const utilidadPct = totalSales > 0 ? (utilidadNeta / totalSales) * 100 : 0;

  // Semáforo de color para cada rubro comparado con su referencia
  const getRefColor = (pct, reference) => {
    const [min, max] = reference.split('-').map(s => parseFloat(s));
    if (pct <= max) return 'text-green-600';
    if (pct <= max * 1.2) return 'text-yellow-600';
    return 'text-red-600';
  };

  // ── Limpiar filtros ────────────────────────────────────────────────────────
  const handleClear = () => {
    setStartDate(''); setEndDate('');
    manuallyEdited.current.clear();
    setRubros(DEFAULT_RUBROS.map(r => ({ ...r })));
  };

  // ── Guardar reporte ────────────────────────────────────────────────────────
  const handleSaveReport = async () => {
    if (!startDate || !endDate) { showMsg('error', 'Selecciona un rango de fechas.'); return; }
    const name = prompt('Nombre del reporte:');
    if (!name) return;

    const payload = {
      name, startDate, endDate,
      totalSales,
      totalExpenses: totalCostos,
      profit: utilidadNeta,
      salesDetails: filteredSales.map(s => ({
        studentName: s.studentName || '',
        date: s.date, total: s.total, products: s.products || [],
      })),
      expenseDetails: rubros.map(r => ({ name: r.name, amount: r.value })),
    };

    try {
      const res  = await fetch('/api/save_financial_report.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Error al guardar');
      showMsg('success', `Reporte "${name}" guardado.`);
      const listRes  = await fetch('/api/list_financial_reports.php', { credentials: 'include' });
      const listData = await listRes.json();
      setSavedReports(Array.isArray(listData) ? listData : []);
    } catch (err) { showMsg('error', err.message); }
  };

  // ── Cargar reporte ─────────────────────────────────────────────────────────
  const handleLoadReport = async id => {
    try {
      const res = await fetch(`/api/get_financial_report.php?id=${id}`, { credentials: 'include' });
      const rpt = await res.json();
      if (!rpt.success) throw new Error(rpt.message);

      setStartDate(rpt.start_date || '');
      setEndDate(rpt.end_date   || '');
      setTotalSales(parseFloat(rpt.total_sales) || 0);

      const loaded = DEFAULT_RUBROS.map(def => {
        const saved = (rpt.expense_details || []).find(e => e.name === def.name);
        return { ...def, value: saved ? parseFloat(saved.amount) : 0 };
      });
      setRubros(loaded);
      manuallyEdited.current = new Set(loaded.map(r => r.id));
      showMsg('success', `Reporte "${rpt.name}" cargado.`);
    } catch (err) { showMsg('error', err.message); }
  };

  // ── Eliminar reporte ───────────────────────────────────────────────────────
  const handleDeleteReport = async id => {
    if (!window.confirm('¿Eliminar este reporte?')) return;
    try {
      const res  = await fetch(`/api/delete_financial_report.php?id=${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      showMsg('success', 'Reporte eliminado.');
      const listRes  = await fetch('/api/list_financial_reports.php', { credentials: 'include' });
      const listData = await listRes.json();
      setSavedReports(Array.isArray(listData) ? listData : []);
    } catch (err) { showMsg('error', err.message); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <SharedCard title="Finanzas — Estructura de Costos">

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      {/* ── Filtros de fecha ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha inicio</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha fin</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
        </div>
        <div className="flex items-end">
          <button onClick={handleClear}
            className="w-full px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200 transition">
            Limpiar filtros
          </button>
        </div>
        <div className="flex items-end">
          <SharedButton onClick={handleSaveReport} className="w-full text-sm py-2">
            Guardar reporte
          </SharedButton>
        </div>
      </div>

      {/* ── Métricas rápidas ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[#961914] to-red-700 p-4 rounded-xl text-white text-center">
          <p className="text-xs opacity-75 uppercase tracking-wide">Ingresos del período</p>
          <p className="text-xl font-extrabold mt-1">{mxn(totalSales)}</p>
          <p className="text-xs opacity-60 mt-0.5">{filteredSales.length} ventas</p>
        </div>
        <div className="bg-gradient-to-br from-gray-700 to-gray-900 p-4 rounded-xl text-white text-center">
          <p className="text-xs opacity-75 uppercase tracking-wide">Total costos</p>
          <p className="text-xl font-extrabold mt-1">{mxn(totalCostos)}</p>
          <p className="text-xs opacity-60 mt-0.5">
            {totalSales > 0 ? (totalCostos / totalSales * 100).toFixed(1) : '0.0'}% de ingresos
          </p>
        </div>
        <div className={`p-4 rounded-xl text-white text-center col-span-2 md:col-span-1 ${
          utilidadNeta >= 0 ? 'bg-gradient-to-br from-green-600 to-green-800' : 'bg-gradient-to-br from-red-600 to-red-800'
        }`}>
          <p className="text-xs opacity-75 uppercase tracking-wide">Utilidad Neta</p>
          <p className="text-xl font-extrabold mt-1">{mxn(utilidadNeta)}</p>
          <p className="text-xs opacity-60 mt-0.5">Ref: 10–20%</p>
        </div>
      </div>

      {/* ── Tabla de estructura de costos ── */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm mb-8">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left">Rubro</th>
              <th className="py-3 px-4 text-right">Valor</th>
              <th className="py-3 px-4 text-right">% del Ingreso</th>
              <th className="py-3 px-4 text-center">Referencia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {estructuraConPct.map(rubro => (
              <tr key={rubro.id} className="hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">{rubro.name}</span>
                    {rubro.auto && !manuallyEdited.current.has(rubro.id) && (
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium">auto</span>
                    )}
                    {rubro.auto && manuallyEdited.current.has(rubro.id) && (
                      <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded font-medium">editado</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  {rubro.auto && !manuallyEdited.current.has(rubro.id) ? (
                    <div className="flex justify-end">
                      <input
                        type="number" min="0" step="0.01"
                        value={rubro.value}
                        onChange={e => handleRubroChange(rubro.id, e.target.value)}
                        className="w-28 text-right text-sm border border-gray-200 rounded-lg px-2 py-1 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                      />
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <input
                        type="number" min="0" step="0.01"
                        value={rubro.value}
                        onChange={e => handleRubroChange(rubro.id, e.target.value)}
                        className="w-28 text-right text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                      />
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <span className={`text-sm font-semibold ${getRefColor(rubro.pct, rubro.reference)}`}>
                    {rubro.pct.toFixed(2)}%
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{rubro.reference}</span>
                </td>
              </tr>
            ))}

            {/* Fila separadora */}
            <tr className="bg-gray-50 border-t-2 border-gray-300">
              <td className="py-3 px-4 text-sm font-bold text-gray-700">Total Costos</td>
              <td className="py-3 px-4 text-right font-bold text-gray-900">{mxn(totalCostos)}</td>
              <td className="py-3 px-4 text-right">
                <span className="text-sm font-bold text-gray-700">
                  {totalSales > 0 ? (totalCostos / totalSales * 100).toFixed(2) : '0.00'}%
                </span>
              </td>
              <td />
            </tr>

            {/* Utilidad Neta */}
            <tr className={utilidadNeta >= 0 ? 'bg-green-50' : 'bg-red-50'}>
              <td className="py-3 px-4 text-sm font-bold text-gray-900">Utilidad Neta</td>
              <td className={`py-3 px-4 text-right font-extrabold text-lg ${utilidadNeta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {mxn(utilidadNeta)}
              </td>
              <td className={`py-3 px-4 text-right font-bold ${utilidadNeta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {utilidadPct.toFixed(2)}%
              </td>
              <td className="py-3 px-4 text-center">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">10-20%</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Leyenda de colores ── */}
      <div className="flex gap-4 mb-6 text-xs flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Dentro del rango de referencia</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-500 inline-block" /> Ligeramente elevado</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Por encima del rango recomendado</span>
        <span className="flex items-center gap-1"><span className="text-[10px] bg-blue-50 text-blue-600 px-1 rounded font-medium">auto</span> Calculado automáticamente</span>
      </div>

      {/* ── Reportes guardados ── */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3">Reportes guardados</h3>
        {savedReports.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">No hay reportes guardados.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
                  <th className="py-3 px-4 text-left">Nombre</th>
                  <th className="py-3 px-4 text-left">Período</th>
                  <th className="py-3 px-4 text-right">Ingresos</th>
                  <th className="py-3 px-4 text-right">Utilidad</th>
                  <th className="py-3 px-4 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {savedReports.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-semibold text-gray-900">{r.name}</td>
                    <td className="py-3 px-4 text-xs text-gray-500">{r.start_date} – {r.end_date}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{mxn(r.total_sales)}</td>
                    <td className={`py-3 px-4 text-right text-sm font-semibold ${parseFloat(r.net_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {mxn(r.net_profit)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => handleLoadReport(r.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                          Cargar
                        </button>
                        <button onClick={() => handleDeleteReport(r.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SharedCard>
  );
};

export default FinancesPanel;